import { useId, useState } from "react";
import { supabase } from "../supabaseClient";
import { readWithTimeout, settleWithTimeout } from "../lib/supabaseRead";
import { signOutAndClearSiteScopeSession } from "../lib/authSession-v2";
import { PASSWORD_MIN_LENGTH, PASSWORD_RESET_CONFIRMATION_MESSAGE } from "../lib/userMessages";
import { buildAuthUrl, consumeShareRedirect, getSafeRedirectFromSearch } from "../lib/deepLinks";

const T = {
  bg: "#0a0f1a",
  bgPanel: "#0e1425",
  bgCard: "#141a2a",
  border: "#1e2538",
  accent: "#c0a060",
  accentHover: "#d0b170",
  text: "#e8e0d8",
  textMuted: "#aaa09a",
  textDark: "#0a0f1a",
  danger: "#e85050",
};
const RESET_COOLDOWN_MS = 60 * 1000;
const RESET_COOLDOWN_KEY = "ss_password_reset_last_sent";
const TERMS_VERSION = "2026-05-05";
const PRIVACY_VERSION = "2026-04-22";

const iStyle = (focus) => ({
  width: "100%",
  padding: "12px 14px",
  borderRadius: 8,
  border: `1px solid ${focus ? T.accent : T.border}`,
  background: T.bgCard,
  color: T.text,
  fontSize: "var(--text-input, 16px)",
  fontFamily: "Calibri, sans-serif",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
});

function InputField({ label, type, value, onChange, placeholder, autoComplete, showToggle, required = false }) {
  const [focus, setFocus] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const inputId = useId();
  const effectiveType = showToggle && type === "password" ? (showPwd ? "text" : "password") : type;
  return (
    <div style={{ marginBottom: 16 }}>
      <label
        htmlFor={inputId}
        style={{
          display: "block",
          fontFamily: "Calibri, sans-serif",
          fontSize: 13,
          color: T.textMuted,
          marginBottom: 6,
          letterSpacing: 0.3,
        }}
      >
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          id={inputId}
          type={effectiveType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={{ ...iStyle(focus), paddingRight: showToggle ? 42 : 14 }}
        />
        {showToggle && type === "password" && (
          <button
            type="button"
            onClick={() => setShowPwd(p => !p)}
            aria-label={showPwd ? "Hide password" : "Show password"}
            style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 12, fontWeight: 500, letterSpacing: "0.3px", padding: 4, lineHeight: 1 }}
          >
            {showPwd ? "Hide" : "Show"}
          </button>
        )}
      </div>
    </div>
  );
}

function getResetCooldownRemaining(email) {
  try {
    const stored = JSON.parse(localStorage.getItem(RESET_COOLDOWN_KEY) || "{}");
    const lastSentAt = Number(stored[email.trim().toLowerCase()] || 0);
    return Math.max(0, RESET_COOLDOWN_MS - (Date.now() - lastSentAt));
  } catch {
    return 0;
  }
}

function markResetSent(email) {
  try {
    const key = email.trim().toLowerCase();
    const stored = JSON.parse(localStorage.getItem(RESET_COOLDOWN_KEY) || "{}");
    localStorage.setItem(RESET_COOLDOWN_KEY, JSON.stringify({ ...stored, [key]: Date.now() }));
  } catch {
    /* ignore storage failures */
  }
}

function isExpiredRecoveryError(err) {
  const msg = String(err?.message || err || '').toLowerCase();
  return msg.includes('expired') || msg.includes('invalid') || msg.includes('missing') || msg.includes('jwt') || msg.includes('session');
}

export default function AuthPage({ initialMode }) {
  const initialVoucherCode = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("voucher") || params.get("code") || "";
    } catch {
      return "";
    }
  })();
  const [mode, setMode] = useState(() => {
    if (initialMode) return initialMode;
    if (window.location.pathname === '/reset-password') return 'update';
    const params = new URLSearchParams(window.location.search);
    if (params.get('signed_out') === '1') return 'signin';
    const p = params.get('auth');
    return p === 'signup' ? 'signup' : 'signin';
  }); // "signin" | "signup" | "reset" | "update"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [accessCode, setAccessCode] = useState(initialVoucherCode);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [btnHover, setBtnHover] = useState(false);

  const setAuthMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setMessage("");
    setConfirmPassword("");
    setCompanyName("");
    setTermsAccepted(false);
    const nextAuth = nextMode === 'signup' ? 'signup' : 'signin';
    window.history.replaceState({}, "", buildAuthUrl(nextAuth, getSafeRedirectFromSearch(window.location.search)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    try {
      if (mode === "signup" && !fullName.trim()) {
        setError("Full name is required.");
        setLoading(false);
        return;
      }
      if (mode === "signup" && !companyName.trim()) {
        setError("Company name is required.");
        setLoading(false);
        return;
      }
      if ((mode === "signup" || mode === "update") && password !== confirmPassword) {
        setError("Passwords don't match.");
        setLoading(false);
        return;
      }
      if ((mode === "signup" || mode === "update") && password.length < PASSWORD_MIN_LENGTH) {
        setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
        setLoading(false);
        return;
      }
      if (mode === "signup" && !termsAccepted) {
        setError("Please agree to the Terms of Use and Privacy Policy to create an account.");
        setLoading(false);
        return;
      }

      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        const redirectUrl = consumeShareRedirect();
        window.history.replaceState({}, "", "/");
        // Redirect back to shared DA link if user came from a public preview or email deep link.
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
      } else if (mode === "signup") {
        const cleanAccessCode = accessCode.trim().toUpperCase();
        if (cleanAccessCode) {
          const { data, error } = await settleWithTimeout(
            supabase.functions.invoke("validate-signup-access-code", {
              body: { code: cleanAccessCode },
            }),
            { label: 'signup_access_code' }
          );
          if (error) throw error;
          if (!data?.valid) {
            setError("That code is not valid. Please check it and try again.");
            setLoading(false);
            return;
          }
        }
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              company: companyName,
              terms_accepted: true,
              terms_version: TERMS_VERSION,
              privacy_version: PRIVACY_VERSION,
              ...(cleanAccessCode ? { signup_code: cleanAccessCode } : {}),
            },
          },
        });
        if (error) throw error;
        setMessage(cleanAccessCode
          ? "Check your email to confirm your account, then sign in. Your code will be applied automatically."
          : "Check your email to confirm your account, then sign in.");
        setMode("signin");
        setPassword("");
      } else if (mode === "reset") {
        const remainingMs = getResetCooldownRemaining(email);
        if (remainingMs > 0) {
          setError(`Please wait ${Math.ceil(remainingMs / 1000)} seconds before requesting another reset link.`);
          setLoading(false);
          return;
        }
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) console.warn('[SiteScope] Password reset request failed:', error.message || error);
        markResetSent(email);
        setMessage(PASSWORD_RESET_CONFIRMATION_MESSAGE);
        setMode("signin");
      } else if (mode === "update") {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
        await signOutAndClearSiteScopeSession();
        setPassword("");
        setConfirmPassword("");
        setMessage("Password updated. Please sign in with your new password.");
        setMode("signin");
        window.history.replaceState({}, "", "/");
      }
    } catch (err) {
      if (mode === "reset") {
        console.warn('[SiteScope] Password reset request failed:', err.message || err);
        markResetSent(email);
        setMessage(PASSWORD_RESET_CONFIRMATION_MESSAGE);
        setMode("signin");
      } else if (mode === "update" && isExpiredRecoveryError(err)) {
        setError("This reset link has expired or already been used. Request a new password reset link.");
        setMode("reset");
        setPassword("");
        setConfirmPassword("");
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const title = mode === "signin" ? "Sign In" : mode === "signup" ? "Create Account" : mode === "update" ? "Set New Password" : "Reset Password";
  const btnLabel = loading
    ? "Please wait…"
    : mode === "signin"
    ? "Sign In"
    : mode === "signup"
    ? "Create Account"
    : mode === "update"
    ? "Update Password"
    : "Send Reset Link";

  return (
    <div
      style={{
        minHeight: "100vh",
        overflowY: "auto",
        background: `radial-gradient(ellipse at center, rgba(192,160,96,0.04) 0%, transparent 65%), ${T.bg}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        boxSizing: "border-box",
        fontFamily: "Calibri, sans-serif",
      }}
    >
      {/* Logo */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: "18.5%",
            background: "linear-gradient(145deg, #c0a060, #8a7040, #a89050)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 26,
            fontWeight: "700",
            color: "#0b0f1a",
            fontFamily: "'DM Sans', sans-serif",
            border: "1.5px solid #c0a060",
          }}
        >
          S
        </div>
        <div>
          <div
            style={{
              fontSize: 22,
              fontWeight: "700",
              color: T.text,
              fontFamily: "Georgia, serif",
              letterSpacing: 0.5,
            }}
          >
            SiteScope
          </div>
          <div
            style={{
              fontSize: 11,
              color: T.textMuted,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
            }}
          >
            DA Tracking & Alerts
          </div>
        </div>
      </div>

      {/* Card */}
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: T.bgPanel,
          borderRadius: 16,
          border: `1px solid ${T.border}`,
          padding: "32px 28px",
        }}
      >
        <h1
          style={{
            margin: "0 0 24px",
            fontFamily: "Georgia, serif",
            fontSize: 22,
            fontWeight: "normal",
            color: T.text,
            letterSpacing: 0.3,
          }}
        >
          {title}
        </h1>

        {message && (
          <div
            style={{
              background: "rgba(42,157,90,0.12)",
              border: "1px solid rgba(42,157,90,0.3)",
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 20,
              fontSize: 14,
              color: "#2a9d5a",
              lineHeight: 1.5,
            }}
          >
            {message}
          </div>
        )}

        {error && (
          <div
            style={{
              background: "rgba(232,80,80,0.1)",
              border: "1px solid rgba(232,80,80,0.3)",
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 20,
              fontSize: 14,
              color: T.danger,
              lineHeight: 1.5,
            }}
          >
            {error}
          </div>
        )}

        {mode === "signup" && <p style={{ color: T.textMuted, fontSize: 13, lineHeight: 1.5 }}>Free Standard includes public DA search, the map, 1 tracked application and 1 watch zone. No card required. Name and company are required. You can switch off the email digest in Settings.</p>}
        <form onSubmit={handleSubmit}>
          {mode === "signup" && (
            <>
              <InputField
                label="Full Name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Lee Builder"
                autoComplete="name"
                required
              />
              <InputField
                label="Company Name"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Builder & Co."
                autoComplete="organization"
                required
              />
              <InputField
                label="Voucher or Referral Code"
                type="text"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                placeholder="Optional"
                autoComplete="off"
              />
            </>
          )}
          {mode !== "update" && (
            <InputField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
          )}
          {mode !== "reset" && (
            <>
              <InputField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "signin" ? "Your password" : "Choose a strong password"}
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                showToggle
                required
              />
              {(mode === "signup" || mode === "update") && (
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: -10, marginBottom: 12 }}>
                  Minimum {PASSWORD_MIN_LENGTH} characters
                </div>
              )}
            </>
          )}
          {(mode === "signup" || mode === "update") && (
            <InputField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              autoComplete="new-password"
              showToggle
              required
            />
          )}

          {mode === "signup" && (
            <>
              <div style={{ margin: "2px 0 10px", padding: "9px 11px", border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, fontSize: 12, lineHeight: 1.45 }}>
                Free Standard includes one weekly DA digest by default. Every email has a one-click unsubscribe, and you can switch the digest off in Settings.
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 9, margin: "2px 0 12px", color: T.textMuted, fontSize: 12, lineHeight: 1.45, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(event) => setTermsAccepted(event.target.checked)}
                  required
                  style={{ marginTop: 2, accentColor: T.accent }}
                />
                <span>
                  I agree to the <a href="/terms" target="_blank" rel="noreferrer" style={{ color: T.accent }}>Terms of Use</a> and acknowledge the <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: T.accent }}>Privacy Policy</a>.
                </span>
              </label>
            </>
          )}

          {(mode === "signup" || mode === "update") && confirmPassword && password !== confirmPassword && (
            <div style={{
              fontSize: 13,
              color: T.danger,
              marginBottom: 12,
              marginTop: -8,
            }}>
              Passwords don't match.
            </div>
          )}

          <button
            type="submit"
            disabled={loading || ((mode === "signup" || mode === "update") && confirmPassword !== "" && password !== confirmPassword)}
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
            style={{
              width: "100%",
              padding: "13px",
              borderRadius: 8,
              border: "none",
              background: loading || ((mode === "signup" || mode === "update") && confirmPassword !== "" && password !== confirmPassword)
                ? `${T.accent}55`
                : btnHover
                ? T.accentHover
                : T.accent,
              color: T.textDark,
              fontSize: 15,
              fontWeight: "600",
              fontFamily: "Calibri, sans-serif",
              cursor: loading || ((mode === "signup" || mode === "update") && confirmPassword !== "" && password !== confirmPassword) ? "default" : "pointer",
              transition: "background 0.2s",
              letterSpacing: 0.3,
              marginTop: 8,
            }}
          >
            {btnLabel}
          </button>
        </form>

        {/* Footer links */}
        <div
          style={{
            marginTop: 20,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            alignItems: "center",
          }}
        >
          {mode === "signin" && (
            <>
              <button
                onClick={() => setAuthMode("signup")}
                style={{ background: "none", border: "none", color: T.accent, fontSize: 13, cursor: "pointer", fontFamily: "Calibri, sans-serif" }}
              >
                No account? Sign up
              </button>
              <button
                onClick={() => setAuthMode("reset")}
                style={{ background: "none", border: "none", color: T.textMuted, fontSize: 13, cursor: "pointer", fontFamily: "Calibri, sans-serif" }}
              >
                Forgot password?
              </button>
            </>
          )}
          {(mode === "signup" || mode === "reset" || mode === "update") && (
            <button
              onClick={() => setAuthMode("signin")}
              style={{ background: "none", border: "none", color: T.textMuted, fontSize: 13, cursor: "pointer", fontFamily: "Calibri, sans-serif" }}
            >
              Back to sign in
            </button>
          )}
        </div>
      </div>

      <div
        style={{
          marginTop: 24,
          fontSize: 12,
          color: T.textMuted,
          fontFamily: "Calibri, sans-serif",
          textAlign: "center",
        }}
      >
        <div style={{ opacity: 0.5 }}>See It First.</div>
        <div style={{ marginTop: 10, display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
          <a href="/councils/" style={{ color: T.textMuted, opacity: 0.72, textDecoration: "none" }}>Browse public DA pages</a>
          <a href="/privacy" style={{ color: T.textMuted, opacity: 0.72, textDecoration: "none" }}>Privacy</a>
          <a href="/terms" style={{ color: T.textMuted, opacity: 0.72, textDecoration: "none" }}>Terms</a>
        </div>
        <div style={{ marginTop: 4 }}>© 2026 SiteScope</div>
      </div>
    </div>
  );
}
